import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAcademicContent,
  getContentFilters,
  isContentSuccess,
  formatFileSize,
  AcademicContentItem,
  ContentFilterOptions
} from '../utils/academicContentApiClient';
import '../styles/AcademicContent.css';

const PAGE_SIZE = 12;

/**
 * Content types get a colour so a student scanning for mind maps finds them
 * without reading every card. Keyed by name rather than id because the ids are
 * seeded and could differ between environments.
 */
const TYPE_CLASS: Record<string, string> = {
  'Detailed Notes': 'type-detailed',
  'Revision Notes': 'type-revision',
  'Mind Maps': 'type-mindmap',
  'Flash Cards': 'type-flashcard',
  'Concept Videos': 'type-video'
};

/** Icons for the quick-type buttons. Unknown types fall back to a folder. */
const TYPE_ICON: Record<string, string> = {
  'Detailed Notes': 'fa-book',
  'Revision Notes': 'fa-bolt',
  'Mind Maps': 'fa-project-diagram',
  'Flash Cards': 'fa-clone',
  'Concept Videos': 'fa-play-circle'
};

/**
 * Three kinds now, not two: a video, an uploaded PDF, or an uploaded image.
 *
 * Read from file_type on the row rather than from the content type, because
 * since migration 0011 one type holds both -- a JPG mind map for one chapter
 * and a PDF for the next.
 */
const iconFor = (item: AcademicContentItem): string => {
  if (item.media === 'video') return 'fa-play-circle';
  if (item.file_type === 'image') return 'fa-file-image';
  return 'fa-file-pdf';
};

/** What tapping the card will do, said plainly before it happens. */
const actionLabel = (item: AcademicContentItem): string => {
  if (item.media === 'video') return 'Watch video';

  const size = item.file_size_bytes ? ` · ${formatFileSize(item.file_size_bytes)}` : '';
  return item.file_type === 'image' ? `View image${size}` : `Open PDF${size}`;
};

const AcademicContent: React.FC = () => {
  // Filters live in the URL so a student can bookmark "NEET / 12 / Physics"
  // and share it. It also means the browser back button steps back through
  // filter changes, which is what people expect from a catalogue.
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<AcademicContentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<ContentFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The item currently open in the lightbox, or null. Only image content ever
  // gets here -- PDFs and videos still open in a new tab, where the browser's
  // own reader and the video host's player do a better job than we could.
  const [lightbox, setLightbox] = useState<AcademicContentItem | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const examId = searchParams.get('exam_id') || '';
  const classGroupId = searchParams.get('class_group_id') || '';
  const subjectId = searchParams.get('subject_id') || '';
  const contentTypeId = searchParams.get('content_type_id') || '';
  const chapterId = searchParams.get('chapter_id') || '';

  const hasFilters = !!(examId || classGroupId || subjectId || contentTypeId || chapterId);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    // Changing the subject invalidates the chapter: chapters belong to one
    // subject, so keeping the old one would filter to an empty list.
    if (key === 'subject_id') {
      next.delete('chapter_id');
    }
    // Any filter change returns to page one. Staying on page 4 of a narrower
    // result set shows an empty grid that looks like a failure.
    next.delete('page');
    setSearchParams(next);
  };

  const clearFilters = () => setSearchParams(new URLSearchParams());

  const goToPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Lookups are fetched once and then held. They change only when an admin
  // adds a subject, which is not something a student session needs to notice.
  useEffect(() => {
    let cancelled = false;
    getContentFilters()
      .then((response: any) => {
        if (cancelled) return;
        if (isContentSuccess(response)) {
          setFilters(response.result);
        }
      })
      .catch(() => {
        // A failed lookup fetch leaves the filter bar empty but must not stop
        // the library itself from rendering.
        if (!cancelled) setFilters(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadContent = useCallback(() => {
    setLoading(true);
    setError(null);

    getAcademicContent({
      exam_id: examId,
      class_group_id: classGroupId,
      subject_id: subjectId,
      content_type_id: contentTypeId,
      chapter_id: chapterId,
      page,
      pagesize: PAGE_SIZE
    })
      .then((response: any) => {
        if (isContentSuccess(response)) {
          setItems(response.result || []);
          setTotal(response.total || 0);
        } else {
          setItems([]);
          setTotal(0);
        }
      })
      .catch(() => {
        setError('We could not load the content library. Please check your connection and try again.');
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [examId, classGroupId, subjectId, contentTypeId, chapterId, page]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Chapters are filtered in the browser. The API returns them all with their
  // subject_id so picking a subject does not cost a round trip.
  const chaptersForSubject = (filters?.chapter || []).filter(
    (c: any) => !subjectId || String(c.subject_id) === String(subjectId)
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openItem = (item: AcademicContentItem) => {
    // An image is the one kind we can show better ourselves. Opening a JPG in
    // a bare tab loses the title and the way back, and on a phone it lands at
    // whatever zoom the browser picks.
    if (item.media !== 'video' && item.file_type === 'image' && item.file_url) {
      setLightbox(item);
      return;
    }

    const url = item.media === 'video' ? item.video_url : item.file_url;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Escape closes the lightbox. Bound only while it is open, so the page has
  // no stray key handler the rest of the time.
  useEffect(() => {
    if (!lightbox) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightbox]);

  return (
    <div className="academic-content-page min-vh-100">
      <div className="container py-5">

        <div className="row mb-4">
          <div className="col-12">
            <h2 className="fw-bold mb-2">
              <i className="fas fa-book-open me-3 text-primary"></i>
              Academic Content
            </h2>
            <p className="text-muted mb-0">
              Notes, mind maps and flash cards for NEET and JEE, free for every WiZi student.
            </p>
          </div>
        </div>

        {/* Quick type buttons. They drive the same content_type_id param as
            the Content dropdown below, so the two always agree. Tapping the
            active one again goes back to all content. */}
        {(filters?.content_type || []).length > 0 && (
          <div className="card border-0 shadow-sm mb-4 quick-types-card">
            <div className="card-body">
              <h6 className="fw-bold mb-3">
                <i className="fas fa-rocket me-2 text-primary"></i>
                Quick Access
              </h6>
              <div className="quick-types" role="group" aria-label="Filter by content type">
                <button
                  type="button"
                  className={`quick-type type-all ${!contentTypeId ? 'active' : ''}`}
                  aria-pressed={!contentTypeId}
                  onClick={() => setFilter('content_type_id', '')}
                >
                  <span className="quick-type-icon">
                    <i className="fas fa-th-large" aria-hidden="true"></i>
                  </span>
                  <span className="quick-type-label">All Content</span>
                </button>

                {(filters?.content_type || []).map((o: any) => {
                  const active = String(o.id) === contentTypeId;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className={`quick-type ${TYPE_CLASS[o.content_type] || ''} ${active ? 'active' : ''}`}
                      aria-pressed={active}
                      onClick={() => setFilter('content_type_id', active ? '' : String(o.id))}
                    >
                      <span className="quick-type-icon">
                        <i className={`fas ${TYPE_ICON[o.content_type] || 'fa-folder'}`} aria-hidden="true"></i>
                      </span>
                      <span className="quick-type-label">{o.content_type}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="card border-0 shadow-sm mb-4 filter-card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-exam">Exam</label>
                <select
                  id="filter-exam"
                  className="form-select"
                  value={examId}
                  onChange={(e) => setFilter('exam_id', e.target.value)}
                >
                  <option value="">All exams</option>
                  {(filters?.exam || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.exam_name}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-class">Class</label>
                <select
                  id="filter-class"
                  className="form-select"
                  value={classGroupId}
                  onChange={(e) => setFilter('class_group_id', e.target.value)}
                >
                  <option value="">All classes</option>
                  {(filters?.class_group || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.class_group}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-subject">Subject</label>
                <select
                  id="filter-subject"
                  className="form-select"
                  value={subjectId}
                  onChange={(e) => setFilter('subject_id', e.target.value)}
                >
                  <option value="">All subjects</option>
                  {(filters?.subject || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.subject}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-chapter">Chapter</label>
                <select
                  id="filter-chapter"
                  className="form-select"
                  value={chapterId}
                  disabled={!subjectId}
                  onChange={(e) => setFilter('chapter_id', e.target.value)}
                >
                  <option value="">{subjectId ? 'All chapters' : 'Pick a subject first'}</option>
                  {chaptersForSubject.map((o: any) => (
                    <option key={o.id} value={o.id}>{o.chapter_name}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-md-4 col-lg">
                <label className="form-label small text-muted mb-1" htmlFor="filter-type">Content</label>
                <select
                  id="filter-type"
                  className="form-select"
                  value={contentTypeId}
                  onChange={(e) => setFilter('content_type_id', e.target.value)}
                >
                  <option value="">All content</option>
                  {(filters?.content_type || []).map((o: any) => (
                    <option key={o.id} value={o.id}>{o.content_type}</option>
                  ))}
                </select>
              </div>
            </div>

            {hasFilters && (
              <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                  <i className="fas fa-times me-2"></i>Clear filters
                </button>
                <span className="text-muted small">
                  {total} {total === 1 ? 'item' : 'items'} match
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading</span>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-danger" onClick={loadContent}>
              Try again
            </button>
          </div>
        )}

        {/* Filters that cannot match anything are reachable on purpose -- exam
            and subject are not narrowed against each other, so Maths under NEET
            is a legal selection with no content. Say that plainly instead of
            showing a bare empty grid. */}
        {!loading && !error && items.length === 0 && (
          <div className="text-center py-5 empty-state">
            <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
            <h5 className="fw-semibold">Nothing published here yet</h5>
            <p className="text-muted mb-3">
              {hasFilters
                ? 'No content matches this combination. Some subjects only appear under one exam.'
                : 'Content will appear here as soon as it is published.'}
            </p>
            {hasFilters && (
              <button type="button" className="btn btn-primary" onClick={clearFilters}>
                Show everything
              </button>
            )}
          </div>
        )}

        {/* Image content, shown in place. Clicking the backdrop or pressing
            Escape closes it; the image itself does not, so a student can tap
            around a mind map without losing it. */}
        {lightbox && (
          <div
            className="content-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={lightbox.title}
            onClick={() => setLightbox(null)}
          >
            <button
              type="button"
              className="content-lightbox-close"
              aria-label="Close"
              onClick={() => setLightbox(null)}
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>

            <figure className="content-lightbox-figure" onClick={(e) => e.stopPropagation()}>
              <img src={lightbox.file_url} alt={lightbox.title} />
              <figcaption>
                <span className="content-lightbox-title">{lightbox.title}</span>
                <a href={lightbox.file_url} target="_blank" rel="noopener noreferrer">
                  Open full size
                </a>
              </figcaption>
            </figure>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="row g-4">
              {items.map((item) => (
                <div className="col-12 col-sm-6 col-lg-4" key={item.id}>
                  <button
                    type="button"
                    className={`content-card ${TYPE_CLASS[item.content_type] || ''}`}
                    onClick={() => openItem(item)}
                  >
                    {/* The uploaded preview when there is one, the type icon
                        when there is not. Every row predates thumbnails, so the
                        icon path stays the normal path until admins fill them
                        in. */}
                    {item.thumbnail_url ? (
                      <div className="content-card-thumb">
                        <img src={item.thumbnail_url} alt="" loading="lazy" />
                        <span className="type-chip type-chip-overlay">{item.content_type}</span>
                      </div>
                    ) : (
                      <div className="content-card-top">
                        <span className="type-chip">{item.content_type}</span>
                        <i
                          className={`fas ${iconFor(item)} content-card-icon`}
                          aria-hidden="true"
                        ></i>
                      </div>
                    )}

                    <h6 className="content-card-title">{item.title}</h6>

                    {item.description && (
                      <p className="content-card-desc">{item.description}</p>
                    )}

                    <div className="content-card-meta">
                      <span>{item.subject}</span>
                      <span className="dot">·</span>
                      <span>Class {item.class_group}</span>
                      <span className="dot">·</span>
                      <span>{item.exam_name}</span>
                    </div>

                    {item.chapter_id ? (
                      <div className="content-card-chapter">
                        <i className="fas fa-bookmark me-2"></i>{item.chapter_name}
                      </div>
                    ) : null}

                    <div className="content-card-action">
                      {actionLabel(item)}
                      <i className="fas fa-arrow-right ms-2"></i>
                    </div>
                  </button>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="mt-5 d-flex justify-content-center" aria-label="Content pages">
                <ul className="pagination mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                      Previous
                    </button>
                  </li>
                  <li className="page-item disabled">
                    <span className="page-link">Page {page} of {totalPages}</span>
                  </li>
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AcademicContent;
